import {
  CardProps,
  Divider,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/joy';
import { WarningCircle } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import {
  SideTabMenu,
  LocalizationFeature,
  WarningChip,
  SaveCancelButtons,
  useLocalizationForm,
} from './MultiLanguageModal';

import { useTenantSlug } from '../tenant/helpers';

import { Card } from '~/components/ui/card';
import { FieldGroup, Label } from '~/components/ui/fieldset';
import { Field, useFormController } from '~/components/ui/form';
import { InputField, inputFieldClasses } from '~/components/ui/input';
import {
  EditorControls,
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { TextAreaField } from '~/components/ui/textarea';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ViewAllFAQ } from '~/pages/customer/settings/program/company-policy/PolicyTabField';
import { LocalizationService } from '~/services/api/localization/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { TabType } from '~/services/api/tenant/types';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

const baseTranslationSchema = z.object({
  id: z.number().nullable(),
  main_id: z.number(),
  language_id: z.number(),
});

export function TranslateProgram() {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();

  const form = useLocalizationForm();
  const isOriginal = form.watch('is_original');
  const selectedLanguage = form.watch('selected_language');
  const selectedFeature = form.watch('selected_feature');
  const isEditing = form.watch('is_editing');
  const setSelectedFeature = (val: LocalizationFeature | string) =>
    form.setValue('selected_feature', val);
  const setProgramTabId = (val: number[]) =>
    form.setValue('program_tabs_ids', val);

  const localizedTenantQuery =
    LocalizationService.endpoints.getLocalizedTenantData.useQuery();
  const localizedChannelQuery =
    LocalizationService.endpoints.otherChannelData.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );
  const localizedProgramTabsQuery =
    LocalizationService.endpoints.programTabs.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );
  const localizedFAQTabQuery = LocalizationService.endpoints.faqs.useQuery(
    {
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
    },
    { skip: !slug || !selectedChannel },
  );

  if (!selectedLanguage)
    return (
      <div className='flex justify-center'>
        <FormattedMessage defaultMessage='To view the translation of a language, kindly select a language first' />
      </div>
    );

  return (
    <div className='flex flex-row gap-4'>
      <div className='basis-96 flex flex-col gap-4 overflow-y-auto max-h-[700px] pr-2'>
        <WarningChip>
          <FormattedMessage defaultMessage="Changes made on the translated version won't affect the original content" />
        </WarningChip>
        {/* ALL PROGRAM TABS */}
        <QueryResolver query={localizedProgramTabsQuery}>
          {(data) => (
            <>
              {data.map((tab) => {
                if (
                  [
                    TabType.INFOGRAPHIC,
                    TabType.PDF,
                    TabType.VIDEO,
                    TabType.FAQ,
                  ].includes(tab.tab_type)
                ) {
                  return null;
                }
                return (
                  <SideTabMenu
                    key={`tab-${tab.title}-${tab.id}`}
                    title={
                      tab.tab_type === TabType.POLICY
                        ? intl.formatMessage({
                            defaultMessage: 'Program Policy',
                          })
                        : tab.title
                    }
                    description={
                      tab.tab_type === TabType.POLICY
                        ? intl.formatMessage({
                            defaultMessage:
                              'View and make changes to the translated version of the program policy',
                          })
                        : intl.formatMessage({
                            defaultMessage:
                              'View and make changes to the translated version of this Program Tab',
                          })
                    }
                    isActive={selectedFeature === `program-tab-${tab.id}`}
                    disabled={isEditing}
                    onClick={() => {
                      setSelectedFeature(`program-tab-${tab.id}`);
                      setProgramTabId([tab.id]);
                    }}
                  />
                );
              })}
              {data.find((tab) => tab.tab_type === TabType.FAQ) && (
                <SideTabMenu
                  title={intl.formatMessage({ defaultMessage: 'FAQ' })}
                  description={intl.formatMessage({
                    defaultMessage:
                      'View and make changes to the translated version of the FAQ program tab',
                  })}
                  isActive={selectedFeature === LocalizationFeature.FAQ}
                  disabled={isEditing}
                  onClick={() => setSelectedFeature(LocalizationFeature.FAQ)}
                />
              )}
            </>
          )}
        </QueryResolver>
        {/* HOME PAGE BIO */}
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Homepage Bio' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the Homepage Bio',
          })}
          isActive={selectedFeature === LocalizationFeature.BIO}
          disabled={isEditing}
          onClick={() => setSelectedFeature(LocalizationFeature.BIO)}
        />
        {/* CHANNEL KEYPOINTS */}
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Key Points' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the Key points',
          })}
          isActive={selectedFeature === LocalizationFeature.KEY_POINTS}
          disabled={isEditing}
          onClick={() => {
            setSelectedFeature(LocalizationFeature.KEY_POINTS);
          }}
        />
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Customize Banner' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the customize banner',
          })}
          isActive={selectedFeature === LocalizationFeature.CUSTOMIZE_BANNER}
          disabled={isEditing}
          onClick={() =>
            setSelectedFeature(LocalizationFeature.CUSTOMIZE_BANNER)
          }
        />
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Message Template' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the message template',
          })}
          isActive={selectedFeature === LocalizationFeature.MESSAGE_TEMPLATE}
          disabled={isEditing}
          onClick={() =>
            setSelectedFeature(LocalizationFeature.MESSAGE_TEMPLATE)
          }
        />
      </div>
      <Divider orientation='vertical' />
      <div className='flex-1 h-[700px]'>
        <QueryResolver query={localizedProgramTabsQuery}>
          {(data) => (
            <>
              {data.map((tab) => {
                let translatedData = undefined;
                Object.entries(tab.translations).forEach(([, value]) => {
                  if (value.language_id === selectedLanguage) {
                    translatedData = value;
                  }
                });
                if (!translatedData)
                  translatedData = {
                    title: '',
                    content: '',
                    id: null,
                  };
                if (
                  [
                    TabType.INFOGRAPHIC,
                    TabType.PDF,
                    TabType.VIDEO,
                    TabType.FAQ,
                  ].includes(tab.tab_type)
                ) {
                  // IF TAB Tab Type 2 means this is a Overview tab and should not be translated.
                  return null;
                } else {
                  return (
                    <>
                      {selectedFeature === `program-tab-${tab.id}` && (
                        <ProgramPolicyLocalization
                          key={`${selectedLanguage}-${isOriginal}-${tab.id}`}
                          defaultValues={{
                            id: translatedData.id,
                            language_id: selectedLanguage,
                            main_id: tab.id,
                            policy_text: tab.content ?? '',
                            translated_policy_text: translatedData.content,
                            tab_title: tab.title,
                            translated_tab_title: translatedData.title,
                            can_update_title: true,
                          }}
                        />
                      )}
                    </>
                  );
                }
              })}
              {data.find((tab) => tab.tab_type === TabType.FAQ) &&
                selectedFeature === LocalizationFeature.FAQ && (
                  <QueryResolver query={localizedFAQTabQuery}>
                    {(faqData) => (
                      <TranslateFAQs
                        key={`${selectedLanguage}-${isOriginal}-faq`}
                        defaultValues={{
                          language_id: selectedLanguage,
                          data: faqData.map((item) => {
                            let translatedData = undefined;
                            Object.entries(item.translations).forEach(
                              ([, value]) => {
                                if (value.language_id === selectedLanguage) {
                                  translatedData = value;
                                }
                              },
                            );
                            if (!translatedData)
                              translatedData = {
                                question: '',
                                answer: '',
                                id: null,
                              };
                            return {
                              id: translatedData.id,
                              main_id: item.id,
                              question: item.question,
                              answer: item.answer,
                              translated_question: translatedData.question,
                              translated_answer: translatedData.answer,
                              is_updated: false,
                            };
                          }),
                        }}
                      />
                    )}
                  </QueryResolver>
                )}
            </>
          )}
        </QueryResolver>
        <QueryResolver query={localizedTenantQuery}>
          {(data) => {
            let translatedData = undefined;
            Object.entries(data?.translations).forEach(([, value]) => {
              if (value.language_id === selectedLanguage) {
                translatedData = value;
              }
            });
            if (!translatedData || isOriginal)
              translatedData = {
                bio: '',
                id: null,
              };
            if (selectedFeature === LocalizationFeature.BIO)
              return (
                <TenantBioTranslation
                  key={`${selectedLanguage}-${isOriginal}-bio`}
                  defaultValues={{
                    id: translatedData.id,
                    language_id: selectedLanguage,
                    main_id: data.id,
                    bio: data.bio ?? '',
                    translated_bio: translatedData.bio ?? '',
                  }}
                />
              );
          }}
        </QueryResolver>
        <QueryResolver query={localizedChannelQuery}>
          {(data) => {
            if (data[0] === undefined)
              return (
                <Typography>
                  <FormattedMessage defaultMessage='No Tenant Data Found' />
                </Typography>
              );
            const translations = data[0].translations;
            let translatedData = undefined;
            Object.entries(translations).forEach(([, value]) => {
              if (value.language_id === selectedLanguage) {
                translatedData = value;
              }
            });
            if (!translatedData || isOriginal)
              translatedData = {
                auto_response_message: '',
                banner_heading: '',
                banner_subheading: '',
                key_points: ['', '', ''],
                id: null,
              };
            if (selectedFeature === LocalizationFeature.CUSTOMIZE_BANNER)
              return (
                <CustomizeBannerlocalization
                  key={`${selectedLanguage}-${isOriginal}-banner-message`}
                  defaultValues={{
                    language_id: selectedLanguage,
                    banner_heading: data[0].banner_heading ?? '',
                    banner_subheading: data[0].banner_subheading ?? '',
                    main_id: data[0].id,
                    id: translatedData.id,
                    translated_banner_heading:
                      translatedData.banner_heading ?? '',
                    translated_banner_subheading:
                      translatedData.banner_subheading ?? '',
                  }}
                />
              );
            if (selectedFeature === LocalizationFeature.MESSAGE_TEMPLATE)
              return (
                <MessageTemplateTranslation
                  key={`${selectedLanguage}-${isOriginal}-message-template`}
                  defaultValues={{
                    language_id: selectedLanguage,
                    auto_first_response_message:
                      data[0].auto_response_message ?? '',
                    main_id: data[0].id,
                    id: translatedData.id,
                    translated_auto_first_response_message:
                      translatedData.auto_response_message ?? '',
                  }}
                />
              );
            if (selectedFeature === LocalizationFeature.KEY_POINTS)
              return (
                <KeypointsTranslation
                  key={`${selectedLanguage}-${isOriginal}-keypoints`}
                  defaultValues={{
                    id: translatedData.id,
                    main_id: data[0].id,
                    language_id: selectedLanguage,
                    keypoint1: data[0].key_points ? data[0].key_points[0] : '',
                    translated_keypoint1: translatedData.key_points
                      ? translatedData.key_points[0]
                      : '',
                    keypoint2: data[0].key_points ? data[0].key_points[1] : '',
                    translated_keypoint2: translatedData.key_points
                      ? translatedData.key_points[1]
                      : '',
                    keypoint3: data[0].key_points ? data[0].key_points[2] : '',
                    translated_keypoint3: translatedData.key_points
                      ? translatedData.key_points[2]
                      : '',
                  }}
                />
              );
          }}
        </QueryResolver>
      </div>
    </div>
  );
}

const programPolicySchema = baseTranslationSchema.extend({
  policy_text: z.string(),
  translated_policy_text: z.string(),
  tab_title: z.string().optional(),
  translated_tab_title: z.string().optional(),
  can_update_title: z.boolean(),
});
type ProgramPolicy = z.infer<typeof programPolicySchema>;

function ProgramPolicyLocalization({
  defaultValues,
}: {
  readonly defaultValues: ProgramPolicy;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();

  const { createToastNotification } = useToastNotificationContext();

  const localizationForm = useLocalizationForm();
  const isEditing = localizationForm.watch('is_editing');
  const isOriginal = localizationForm.watch('is_original');
  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);

  const updatePolicy = useWrappedMutation(
    LocalizationService.endpoints.updateCustomTabTranslation.useMutation(),
  );

  const form = useForm({
    defaultValues,
    schema: programPolicySchema,
  });
  const policy = form.watch('policy_text');
  const translatedPolicy = form.watch('translated_policy_text');
  const canUpdateTitle = form.watch('can_update_title');

  const onSubmitHandler = form.handleSubmit(async (data) => {
    if (!data.id) return;
    await updatePolicy.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          id: data.id,
          content: data.translated_policy_text,
          main_id: data.main_id,
          language_id: data.language_id,
          ...(data.can_update_title && { title: data.translated_tab_title }),
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Description updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updatePolicy.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Policy Translation found, please click auto translate first to continue',
        })}
      />
      <div className='flex flex-col gap-2 mt-8'>
        <Typography level='body-sm'>
          <FormattedMessage defaultMessage='Title' />
        </Typography>
        {isEditing && canUpdateTitle ? (
          <InputField control={form.control} field='translated_tab_title' />
        ) : (
          <Typography className='pl-4'>
            {isOriginal
              ? form.watch('tab_title')
              : form.watch('translated_tab_title')}
          </Typography>
        )}
        <Typography level='body-sm'>
          <FormattedMessage defaultMessage='Content' />
        </Typography>
        {isEditing ? (
          <Field
            size='lg'
            control={form.control}
            field='translated_policy_text'
          >
            <div className={clsx(inputFieldClasses)}>
              <div data-slot='control'>
                <RichTextEditorField />
              </div>
            </div>
          </Field>
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: isOriginal
                ? policy.replace(/attached-comment/g, '')
                : translatedPolicy.replace(/attached-comment/g, ''),
            }}
            style={{
              overflowY: 'auto',
              height: '530px',
              color: '#878787',
            }}
            className='tiptap'
          />
        )}
      </div>
    </div>
  );
}

const customizeBannerSchema = baseTranslationSchema.extend({
  banner_heading: z.string(),
  banner_subheading: z.string(),
  translated_banner_heading: z.string(),
  translated_banner_subheading: z.string(),
});
type CustomizeBanner = z.infer<typeof customizeBannerSchema>;

function CustomizeBannerlocalization({
  defaultValues,
}: {
  readonly defaultValues: CustomizeBanner;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateBanner = useWrappedMutation(
    LocalizationService.endpoints.updateTenantTranslation.useMutation(),
  );

  const localizationForm = useLocalizationForm();
  const isEditing = localizationForm.watch('is_editing');
  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);
  const setChannelObjId = useCallback(
    (val: number) => localizationForm.setValue('channel_object_id', val),
    [localizationForm],
  );
  const isOriginal = localizationForm.watch('is_original');

  useEffect(() => {
    setChannelObjId(defaultValues.main_id);
  }, [defaultValues.main_id, setChannelObjId]);

  const form = useForm({
    defaultValues,
    schema: customizeBannerSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (data) => {
    await updateBanner.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          banner_heading: data.translated_banner_heading,
          banner_subheading: data.translated_banner_subheading,
          main_id: data.main_id,
          language_id: data.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Description updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updateBanner.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Banner Details Translation found, please click auto translate first to continue',
        })}
      />

      <div className='flex flex-col gap-2 mt-8'>
        <Typography
          level='body-sm'
          endDecorator={
            <Tooltip
              title={<FormattedMessage defaultMessage='Max 65 Characters' />}
            >
              <WarningCircle size={20} weight='fill' />
            </Tooltip>
          }
        >
          <FormattedMessage defaultMessage='Heading' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_banner_heading'
            control={form.control}
          />
        ) : (
          <Typography>
            {' '}
            {isOriginal
              ? form.watch('banner_heading')
              : form.watch('translated_banner_heading')}
          </Typography>
        )}
        <Typography
          level='body-sm'
          endDecorator={
            <Tooltip
              title={<FormattedMessage defaultMessage='Max 80 Characters' />}
            >
              <WarningCircle size={20} weight='fill' />
            </Tooltip>
          }
        >
          <FormattedMessage defaultMessage='Sub-heading' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_banner_subheading'
            control={form.control}
          />
        ) : (
          <Typography>
            {isOriginal
              ? form.watch('banner_subheading')
              : form.watch('translated_banner_subheading')}
          </Typography>
        )}
      </div>
    </div>
  );
}

const messageTemplateSchema = baseTranslationSchema.extend({
  auto_first_response_message: z.string(),
  translated_auto_first_response_message: z.string(),
});
type MessageTemplate = z.infer<typeof messageTemplateSchema>;

function MessageTemplateTranslation({
  defaultValues,
}: {
  readonly defaultValues: MessageTemplate;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateMessageTemplate = useWrappedMutation(
    LocalizationService.endpoints.updateTenantTranslation.useMutation(),
  );

  const localizationForm = useLocalizationForm();
  const isOriginal = localizationForm.watch('is_original');
  const isEditing = localizationForm.watch('is_editing');
  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);
  const setChannelObjId = useCallback(
    (val: number) => localizationForm.setValue('channel_object_id', val),
    [localizationForm],
  );

  const form = useForm({
    defaultValues,
    schema: messageTemplateSchema,
  });

  useEffect(() => {
    setChannelObjId(defaultValues.main_id);
  }, [defaultValues.main_id, setChannelObjId]);

  const onSubmitHandler = form.handleSubmit(async (data) => {
    await updateMessageTemplate.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          auto_response_message: data.translated_auto_first_response_message,
          main_id: data.main_id,
          language_id: data.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Auto Response Message updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        onSave={onSubmitHandler}
        isLoading={updateMessageTemplate.state.isLoading}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Message Template Translation found, please click auto translate first to continue',
        })}
      />
      {isEditing ? (
        <Field
          size='lg'
          control={form.control}
          field='translated_auto_first_response_message'
        >
          <div className={clsx(inputFieldClasses)}>
            <div data-slot='control'>
              <RichTextEditorField />
            </div>
          </div>
        </Field>
      ) : (
        <div
          dangerouslySetInnerHTML={{
            __html: isOriginal
              ? defaultValues.auto_first_response_message
              : defaultValues.translated_auto_first_response_message,
          }}
          style={{
            overflowY: 'auto',
            height: '600px',
            color: '#878787',
          }}
          className='tiptap'
        />
      )}
    </div>
  );
}

const tenantBioSchema = baseTranslationSchema.extend({
  bio: z.string(),
  translated_bio: z.string(),
});
type TenantBioTemplate = z.infer<typeof tenantBioSchema>;

function TenantBioTranslation({
  defaultValues,
}: {
  readonly defaultValues: TenantBioTemplate;
}) {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const updateHomepaeBio = useWrappedMutation(
    LocalizationService.endpoints.updateLocalizedTenantData.useMutation(),
  );

  const localizationForm = useLocalizationForm();
  const isEditing = localizationForm.watch('is_editing');
  const isOriginal = localizationForm.watch('is_original');
  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);
  const setChannelObjId = useCallback(
    (val: number) => localizationForm.setValue('tenant_id', val),
    [localizationForm],
  );

  useEffect(() => {
    setChannelObjId(defaultValues.main_id);
  }, [defaultValues.main_id, setChannelObjId]);

  const form = useForm({
    defaultValues,
    schema: tenantBioSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (data) => {
    await updateHomepaeBio.mutateAsync(
      {
        payload: {
          bio: data.translated_bio,
          main_id: data.main_id,
          language_id: data.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Bio localization updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        onSave={onSubmitHandler}
        isLoading={updateHomepaeBio.state.isLoading}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Homepage Banner Translation found, please click auto translate first to continue',
        })}
      />
      <div className='flex flex-col gap-2 mt-4'>
        <Typography level='title-lg'>
          <FormattedMessage defaultMessage='Bio' />
        </Typography>
        {isEditing ? (
          <TextAreaField
            control={form.control}
            field='translated_bio'
            size='lg'
            minRows={4}
            placeholder={intl.formatMessage({
              defaultMessage: 'Add your company introduction here...',
            })}
            className='mt-4'
          />
        ) : (
          <Typography>
            {isOriginal ? form.watch('bio') : form.watch('translated_bio')}
          </Typography>
        )}
      </div>
    </div>
  );
}

// TRANSLATE KEY POINTS
const keypointsSchema = baseTranslationSchema.extend({
  keypoint1: z.string(),
  translated_keypoint1: z.string().min(1),
  keypoint2: z.string(),
  translated_keypoint2: z.string().min(1),
  keypoint3: z.string(),
  translated_keypoint3: z.string().min(1),
});
type KeypointsTemplate = z.infer<typeof keypointsSchema>;

function KeypointsTranslation({
  defaultValues,
}: {
  readonly defaultValues: KeypointsTemplate;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();

  const localizationForm = useLocalizationForm();
  const isEditing = localizationForm.watch('is_editing');
  const isOriginal = localizationForm.watch('is_original');

  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);

  const setChannelObjId = useCallback(
    (val: number) => localizationForm.setValue('channel_object_id', val),
    [localizationForm],
  );

  useEffect(() => {
    setChannelObjId(defaultValues.main_id);
  }, [defaultValues.main_id, setChannelObjId]);

  const updateKeypoints = useWrappedMutation(
    LocalizationService.endpoints.updateTenantTranslation.useMutation(),
  );

  const form = useForm({
    defaultValues,
    schema: keypointsSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (data) => {
    await updateKeypoints.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          key_points: [
            data.translated_keypoint1,
            data.translated_keypoint2,
            data.translated_keypoint3,
          ],
          main_id: data.main_id,
          language_id: data.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Keypoints localization updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        onSave={onSubmitHandler}
        isLoading={updateKeypoints.state.isLoading}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Message Template Translation found, please click auto translate first to continue',
        })}
      />
      <div className='flex flex-col gap-2 pt-4'>
        <Typography level='title-md'>
          <FormattedMessage defaultMessage='Keypoint 1' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_keypoint1'
            control={form.control}
          />
        ) : (
          <Typography className='pl-2'>
            {isOriginal
              ? form.watch('keypoint1')
              : form.watch('translated_keypoint1')}
          </Typography>
        )}
        <Typography level='title-md'>
          <FormattedMessage defaultMessage='Keypoint 2' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_keypoint2'
            control={form.control}
          />
        ) : (
          <Typography className='pl-2'>
            {isOriginal
              ? form.watch('keypoint2')
              : form.watch('translated_keypoint2')}
          </Typography>
        )}
        <Typography level='title-md'>
          <FormattedMessage defaultMessage='Keypoint 3' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_keypoint3'
            control={form.control}
          />
        ) : (
          <Typography className='pl-2'>
            {isOriginal
              ? form.watch('keypoint3')
              : form.watch('translated_keypoint3')}
          </Typography>
        )}
      </div>
    </div>
  );
}

const faqSchema = z.object({
  language_id: z.number(),
  data: z.array(
    z.object({
      id: z.number().nullable(),
      main_id: z.number(),
      answer: z.string(),
      question: z.string(),
      translated_answer: z.string(),
      translated_question: z.string(),
      is_updated: z.boolean(),
    }),
  ),
});

type FAQData = z.infer<typeof faqSchema>;

function TranslateFAQs({
  defaultValues,
}: Readonly<{
  defaultValues: FAQData;
}>) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();

  const localizationForm = useLocalizationForm();
  const isEditing = localizationForm.watch('is_editing');
  const isOriginal = localizationForm.watch('is_original');

  const setFAQIds = useCallback(
    (val: number[]) => localizationForm.setValue('faq_items', val),
    [localizationForm],
  );

  useEffect(() => {
    setFAQIds(defaultValues.data.map((data) => data.main_id));
  }, [defaultValues, setFAQIds]);

  const updateFAQItem = useWrappedMutation(
    LocalizationService.endpoints.updateFAQTranslation.useMutation(),
  );

  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);

  const form = useForm({
    defaultValues,
    schema: faqSchema,
  });

  const faqItems = form.watch('data');

  const onSubmitHandler = form.handleSubmit(async (formData) => {
    const updatedTranslations = formData.data.filter(
      (data) => data.main_id !== null,
    );

    const payload = updatedTranslations.map((translation) => ({
      language_id: formData.language_id,
      main_id: translation.main_id,
      question: translation.translated_question,
      answer: translation.translated_answer,
    }));

    updateFAQItem.mutateAsync({
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
      payload: payload,
    });
    setIsEditing();
    createToastNotification(
      intl.formatMessage({ defaultMessage: 'FAQ items updated successfully' }),
      'success',
    );
  });

  return (
    <div>
      <SaveCancelButtons
        onSave={onSubmitHandler}
        isLoading={updateFAQItem.state.isLoading}
        isEditable={defaultValues.data.every((item) => item.id != null)}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Message Template Translation found, please click auto translate first to continue',
        })}
      />
      <div className='flex flex-col gap-2 mt-8 overflow-y-auto h-[620px]'>
        {!isEditing ? (
          <div className='flex flex-col gap-4'>
            <ViewAllFAQ
              title={'FAQ'}
              faqs={faqItems.map((item) => ({
                answer: isOriginal
                  ? item.answer
                  : item.translated_answer || '--',
                question: isOriginal
                  ? item.question
                  : item.translated_question || '--',
                id: item.id ?? 0,
                order: 0,
              }))}
            />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            {faqItems.map((faq, key) => (
              <Card key={`${key}-${faq.id}`}>
                <FieldGroup>
                  <InputField
                    size='lg'
                    control={form.control}
                    field={`data.${key}.translated_question` as const}
                    label={`${intl.formatMessage({
                      defaultMessage: 'FAQ Question',
                    })} ${key + 1}`}
                  />
                  <Field
                    size='lg'
                    control={form.control}
                    field={`data.${key}.translated_answer` as const}
                  >
                    <Label className='!mb-2'>
                      <FormattedMessage
                        defaultMessage='FAQ {number} Answer'
                        values={{
                          number: key + 1,
                        }}
                      />
                    </Label>
                    <div className={clsx(inputFieldClasses)}>
                      <div data-slot='control'>
                        <RichTextEditorField />
                      </div>
                    </div>
                  </Field>
                </FieldGroup>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RichTextEditorField({
  variant = 'outlined',
}: Readonly<Pick<CardProps, 'variant'>>) {
  const controller = useFormController();
  const isInvalid = !!controller?.fieldState.invalid;

  return (
    <RichTextEditor>
      <Card
        size='sm'
        color={isInvalid ? 'danger' : 'neutral'}
        className='h-[520px]'
        variant={variant}
      >
        <RichTextEditorContent />
        <Divider />
        <div className='flex items-center w-full gap-2'>
          <EditorControls.Formatting />
          <Divider orientation='vertical' />
          <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
            <EditorControls.Bold />
            <EditorControls.Underline />
            <EditorControls.Italic />
            <EditorControls.OrderedList />
            <EditorControls.UnorderedList />
          </ToggleButtonGroup>
        </div>
      </Card>
    </RichTextEditor>
  );
}
